import React from 'react';

import {RoundButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {useFeature} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';
import {CustomerAddressType} from 'desktop/types/customerAddressesType';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {getActiveAddress} from '../../store/selectors/deliveryStep/deliveryStep';
import {getAddressComponents} from '../../utils/formatAddress/formatAddress';
import {Addresses} from '../addresses/addresses';
import {CustomerAddressesListConnected} from '../customerAddressesList/customerAddressesList';

import {DeliveryFormCheckboxConnected} from '../deliveryFormCheckbox/deliveryFormCheckbox';

import {getButtonProps} from './getButtonProps';

import s from './customerAddresses.scss';

export type PropsType = {
    type: 'shipping' | 'billing';
    activeAddress?: CustomerAddressType;
};

/**
 * Customer address for checkout
 * @param {PropsType} props - props address components
 * @returns {ReactElement} - Customer address components
 */
export const CustomerAddresses: React.FC<PropsType> = ({activeAddress, type}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {isOneStepAvailable} = useIsOneStepAvailable();

    const navigate = useNavigate();
    if (!activeAddress) return null;

    const addressEditHandler = () => {
        navigate(`/checkout/onepage/address/${type}/edit`);
    };

    const addAddressHandler = () => {
        navigate(`/checkout/onepage/address/${type}/new`);
    };

    return (
        <div
            aria-label="Customer addresses"
            className={classNames(s.customerAddresses, {[s.oneStep]: isOneStepAvailable})}
        >
            <div className={s.selectedAddressWrapper}>
                <div className={s.selectedAddress}>
                    <h3 className={classNames(s.title, {[s.titleFacelift]: isFacelift})}>
                        <span className={s.type}>{type}</span> address
                    </h3>
                    <Addresses addresses={getAddressComponents(activeAddress)} />
                </div>
                <div className={s.addressActions}>
                    <RoundButton
                        {...getButtonProps(isOneStepAvailable || isFacelift)}
                        onClick={addressEditHandler}
                    >
                        Edit
                    </RoundButton>
                    <RoundButton
                        {...getButtonProps(isOneStepAvailable || isFacelift)}
                        onClick={addAddressHandler}
                    >
                        New Address
                    </RoundButton>
                </div>
            </div>
            <CustomerAddressesListConnected type={type} />
            {isOneStepAvailable && type === 'shipping' && <DeliveryFormCheckboxConnected />}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {type}: {type: 'shipping' | 'billing'}) => ({
    activeAddress: getActiveAddress(store, type),
});

export const CustomerAddressesConnected = connect(mapStateToProps)(CustomerAddresses);
